import { useState, type ReactNode } from 'react';
import { Alert } from '../../core/atoms/Alert';
import { Button } from '../../core/atoms/Button';
import { Group } from '../../core/atoms/Group';
import { Paper } from '../../core/atoms/Paper';
import { Stack } from '../../core/atoms/Stack';
import { Text } from '../../core/atoms/Text';
import { TextInput } from '../../core/atoms/TextInput';
import { Select } from '../../core/atoms/Select';
import { BottomDrawer } from '../../core/molecules/BottomDrawer';
import { ConfirmDialog } from '../../core/molecules/ConfirmDialog';
import { FormField } from '../../core/molecules/FormField';
import { MobileListRow } from '../../core/molecules/MobileListRow';
import { PageHeader } from '../../core/molecules/PageHeader';
import { DEFAULT_PAGE_LIMIT } from '../../core/utils/constants';
import type { PaginatedResult, ResourceQuery } from '../../core/types';
import { ResourceLayout } from '../TableLayout/ResourceLayout';

/** Row shape every simple contact entity (labour, transport, warehouse…) shares. */
export interface ContactRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface ResourceCrudField<T extends ContactRow> {
  /** Key on both the row and the form draft. */
  key: string;
  label: string;
  required?: boolean;
  /** Shown as a table column. Defaults to true. */
  column?: boolean;
  /** Reads the initial form value off an existing row. */
  fromRow?: (row: T) => string;
  /** When set, renders a Select instead of TextInput. */
  options?: Array<{ value: string; label: string }>;
}

export interface ResourceListArgs {
  page: number;
  pageLimit: number;
  search?: string;
}

export interface ResourceListResult<T> {
  data: PaginatedResult<T> | undefined;
  isLoading: boolean;
  isFetching?: boolean;
  isError?: boolean;
}

export interface ResourceCrudPageProps<T extends ContactRow> {
  title: string;
  description?: string;
  /** Singular noun used in button labels and drawer titles, e.g. "warehouse". */
  entityLabel: string;
  /** Plural noun used in error / empty copy, e.g. "warehouses". */
  entityLabelPlural?: string;
  fields: ResourceCrudField<T>[];
  /** RTK Query list hook, already narrowed to the paginated payload. */
  useList: (args: ResourceListArgs) => ResourceListResult<T>;
  onSave: (values: Record<string, string>, editing: T | null) => Promise<unknown>;
  onDelete?: (row: T) => Promise<unknown>;
  saving?: boolean;
  canMutate?: boolean;
  /** Extra content rendered under the mobile list row subtitle line. */
  subtitleOf?: (row: T) => string | undefined;
}

function emptyDraft<T extends ContactRow>(fields: ResourceCrudField<T>[]) {
  return Object.fromEntries(fields.map((f) => [f.key, ''])) as Record<string, string>;
}

function draftFromRow<T extends ContactRow>(fields: ResourceCrudField<T>[], row: T) {
  return Object.fromEntries(
    fields.map((f) => [
      f.key,
      f.fromRow ? f.fromRow(row) : String(row[f.key] ?? ''),
    ]),
  ) as Record<string, string>;
}

/**
 * List + drawer-form CRUD screen for flat contact-style entities.
 * Pages supply only their RTK hooks and field metadata.
 */
export function ResourceCrudPage<T extends ContactRow>({
  title,
  description,
  entityLabel,
  entityLabelPlural,
  fields,
  useList,
  onSave,
  onDelete,
  saving,
  canMutate = true,
  subtitleOf,
}: ResourceCrudPageProps<T>) {
  const plural = entityLabelPlural ?? `${entityLabel}s`;
  const [resourceQuery, setResourceQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [form, setForm] = useState<Record<string, string>>(() => emptyDraft(fields));
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);

  const { data, isLoading, isFetching, isError } = useList({
    page: resourceQuery.page,
    pageLimit: resourceQuery.pageLimit,
    search: resourceQuery.search || undefined,
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyDraft(fields));
    setDrawerOpen(true);
  }

  function openEdit(row: T) {
    setEditing(row);
    setForm(draftFromRow(fields, row));
    setDrawerOpen(true);
  }

  async function handleSave() {
    await onSave(form, editing);
    setDrawerOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete || !onDelete) return;
    await onDelete(confirmDelete);
    setConfirmDelete(null);
    setDrawerOpen(false);
  }

  const requiredMissing = fields.some(
    (f) => f.required && !(form[f.key] ?? '').trim(),
  );

  const defaultSubtitle = (row: T) =>
    fields
      .filter((f) => f.key !== 'name')
      .map((f) => String(row[f.key] ?? '').trim())
      .filter(Boolean)
      .join(' · ');

  return (
    <Stack gap="md">
      <PageHeader title={title} description={description} />
      {isError ? <Alert color="red" title={`Failed to load ${plural}`} /> : null}
      <ResourceLayout
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setResourceQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={`Search ${plural}...`}
        emptyTitle={`No ${plural} yet`}
        emptyDescription={`${plural[0].toUpperCase()}${plural.slice(1)} for this organization will show up here.`}
        mobileView="list"
        addButton={
          canMutate ? <Button onClick={openCreate}>Add {entityLabel}</Button> : undefined
        }
        onRowClick={openEdit}
        columns={fields
          .filter((f) => f.column !== false)
          .map((f) => ({
            key: f.key,
            header: f.label,
            render: (row: T) => {
              if (f.options?.length) {
                const match = f.options.find((o) => o.value === String(row[f.key] ?? ''));
                return (match?.label ?? String(row[f.key] ?? '')) || '—';
              }
              return String(row[f.key] ?? '') || '—';
            },
          }))}
        renderCard={(row: T) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Text fw={600}>{row.name}</Text>
              <Text size="sm" c="dimmed">
                {(subtitleOf?.(row) ?? defaultSubtitle(row)) || 'No contact'}
              </Text>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: T) => (
          <MobileListRow
            title={row.name}
            subtitle={(subtitleOf?.(row) ?? defaultSubtitle(row)) || undefined}
            onClick={() => openEdit(row)}
          />
        )}
      />
      <BottomDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={`${editing ? 'Edit' : 'Add'} ${entityLabel}`}
      >
        <Stack gap="md">
          {fields.map((field) => (
            <FormField key={field.key} label={field.label} required={field.required}>
              {field.options ? (
                <Select
                  data={field.options}
                  value={form[field.key] || null}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, [field.key]: value ?? '' }))
                  }
                  searchable
                  clearable={!field.required}
                />
              ) : (
                <TextInput
                  value={form[field.key] ?? ''}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setForm((prev) => ({ ...prev, [field.key]: value }));
                  }}
                />
              )}
            </FormField>
          ))}
          <Group justify="space-between">
            {editing && onDelete ? (
              <Button
                color="red"
                variant="light"
                onClick={() => setConfirmDelete(editing)}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <Button
              loading={saving}
              disabled={requiredMissing}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={`Delete ${entityLabel}?`}
        message={`${confirmDelete?.name ?? ''} will be permanently removed.`}
        confirmLabel="Delete"
        danger
      />
    </Stack>
  );
}
